import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Brain, CheckCircle, Sparkle, Lightbulb, Lightning, ChatCircleText,
  Lock, ArrowCounterClockwise, X,
} from "@phosphor-icons/react";

const PURPOSES = [
  { key: "parse",    label: "Parse Prompt → Oracle JSON",  Icon: ChatCircleText,
    desc: "Turns the natural-language prompt into the Oracle Fusion REST payload. Quality matters most here." },
  { key: "suggest",  label: "Inline Autosuggest",          Icon: Sparkle,
    desc: "Powers the smart chip row under the prompt. Speed + cost matter most." },
  { key: "insights", label: "AI Insights / Narrative",     Icon: Lightbulb,
    desc: "The CFO-readable summary on anomaly / variance / narrative agents." },
];

const TIER_TONE = {
  premium:  "border-purple-300 bg-purple-50 text-purple-700",
  balanced: "border-blue-300 bg-blue-50 text-[#0020C2]",
  fast:     "border-emerald-300 bg-emerald-50 text-emerald-700",
};
const PROVIDER_LABEL = { anthropic: "Anthropic", openai: "OpenAI", google: "Google" };

const ChoiceSelect = ({ value, onChange, catalog, whitelist }) => {
  const sel = `${value?.provider}/${value?.id}`;
  const allowed = whitelist?.length
    ? catalog.filter((m) => whitelist.includes(`${m.provider}/${m.id}`))
    : catalog;
  return (
    <select value={sel}
      onChange={(e) => {
        const [provider, id] = e.target.value.split("/");
        onChange({ provider, id });
      }}
      data-testid="llm-choice-select"
      className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2] bg-white">
      {allowed.map((m) => (
        <option key={`${m.provider}/${m.id}`} value={`${m.provider}/${m.id}`}>
          {m.label} · {PROVIDER_LABEL[m.provider]} · {m.tier}
        </option>
      ))}
    </select>
  );
};

const TenantDrawer = ({ tenantId, settings, catalog, onClose, onSaved }) => {
  const [parse, setParse] = useState(settings?.parse || null);
  const [suggest, setSuggest] = useState(settings?.suggest || null);
  const [insights, setInsights] = useState(settings?.insights || null);
  const [byokProvider, setByokProvider] = useState(settings?.byok_provider || "");
  const [byokKey, setByokKey] = useState("");  // never pre-fill; encrypted
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { byok_provider: byokProvider };
      if (parse)    body.parse = parse;
      if (suggest)  body.suggest = suggest;
      if (insights) body.insights = insights;
      if (byokKey)  body.byok_api_key = byokKey;
      await api.put(`/erp-agents/llm-settings/tenant/${encodeURIComponent(tenantId)}`, body);
      await onSaved();
      onClose();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-300"
        data-testid="tenant-llm-drawer">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">Tenant LLM Override</div>
            <h3 className="text-2xl font-black tracking-tight mt-1 font-mono">{tenantId}</h3>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {PURPOSES.map((p) => (
            <div key={p.key}>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-600 block mb-1.5">
                {p.label}
              </label>
              <ChoiceSelect value={(p.key === "parse" ? parse : p.key === "suggest" ? suggest : insights) || settings?.[p.key]}
                onChange={p.key === "parse" ? setParse : p.key === "suggest" ? setSuggest : setInsights}
                catalog={catalog} whitelist={[]} />
            </div>
          ))}
          <div className="border-t border-gray-200 pt-5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1.5 flex items-center gap-1">
              <Lock size={11} weight="fill" /> Bring Your Own Key (Optional)
            </div>
            <div className="text-[11px] text-gray-500 mb-2.5">
              When set, calls for this tenant use the tenant&apos;s own provider key instead of the Emergent key.
            </div>
            <div className="grid grid-cols-[1fr_2fr] gap-2">
              <select value={byokProvider} onChange={(e) => setByokProvider(e.target.value)}
                data-testid="byok-provider"
                className="border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0020C2]">
                <option value="">— None (use Emergent key) —</option>
                <option value="anthropic">Anthropic</option>
                <option value="openai">OpenAI</option>
                <option value="google">Google</option>
              </select>
              <input type="password" value={byokKey} onChange={(e) => setByokKey(e.target.value)}
                placeholder={settings?.byok_set ? "•••••••• (encrypted, leave blank to keep)" : "Paste API key (stored encrypted)"}
                data-testid="byok-api-key"
                className="border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
          </div>
          {err && <div className="text-xs text-[#DC2626] border border-[#DC2626] bg-red-50 px-3 py-2">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={busy} data-testid="tenant-llm-save"
              className="bg-[#0020C2] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
              {busy ? "Saving…" : "Save tenant override"}
            </button>
            <button onClick={onClose} className="border border-gray-300 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function LlmSettingsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProduct, setSavingProduct] = useState(false);
  const [editTenant, setEditTenant] = useState(null);   // tenant_id string
  const [newTenantId, setNewTenantId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/erp-agents/llm-settings");
      setData(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveProduct = async () => {
    setSavingProduct(true);
    try {
      await api.put("/erp-agents/llm-settings/product", {
        parse: data.product.parse,
        suggest: data.product.suggest,
        insights: data.product.insights,
        whitelist: data.product.whitelist,
      });
      await load();
    } finally { setSavingProduct(false); }
  };

  const clearTenant = async (tid) => {
    if (!window.confirm(`Clear LLM override for ${tid}?`)) return;
    await api.delete(`/erp-agents/llm-settings/tenant/${encodeURIComponent(tid)}`);
    await load();
  };

  const setProduct = (purpose, choice) =>
    setData({ ...data, product: { ...data.product, [purpose]: choice } });
  const toggleWhitelist = (key) => {
    const wl = data.product.whitelist || [];
    const next = wl.includes(key) ? wl.filter((x) => x !== key) : [...wl, key];
    setData({ ...data, product: { ...data.product, whitelist: next } });
  };

  if (loading || !data) return <div className="p-8 text-sm text-gray-500">Loading…</div>;

  return (
    <div>
      <PageHeader
        eyebrow="Admin Console"
        title="LLM Settings"
        subtitle="Pick the language model that powers each part of the agent flow. Set product-wide defaults, lock down which models tenants can use, or attach a tenant's own API key to bill them directly."
        testid="llm-settings-title"
      />
      <div className="p-8 space-y-6">
        {/* ─── Product defaults ─── */}
        <section className="border border-gray-200 bg-white" data-testid="product-llm-section">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Product Defaults</div>
              <h3 className="text-xl font-black tracking-tight mt-0.5">Default model per purpose</h3>
            </div>
            <button onClick={saveProduct} disabled={savingProduct} data-testid="save-product-llm"
              className="bg-[#0020C2] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
              {savingProduct ? "Saving…" : "Save Defaults"}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
            {PURPOSES.map((p) => (
              <div key={p.key} className="border border-gray-200 p-4" data-testid={`product-purpose-${p.key}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <p.Icon size={14} weight="fill" className="text-[#0020C2]" />
                  <div className="text-[11px] font-bold uppercase tracking-wider">{p.label}</div>
                </div>
                <div className="text-[11px] text-gray-500 leading-snug mb-3">{p.desc}</div>
                <ChoiceSelect value={data.product[p.key]} onChange={(c) => setProduct(p.key, c)}
                  catalog={data.catalog} whitelist={[]} />
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 p-6">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">Tenant Whitelist <span className="font-normal normal-case text-gray-400">(leave empty to allow every model)</span></div>
            <div className="text-[11px] text-gray-500 mb-3">Only the models you tick here can be picked at the tenant level. The rest fall back to product defaults.</div>
            <div className="flex flex-wrap gap-2">
              {data.catalog.map((m) => {
                const key = `${m.provider}/${m.id}`;
                const on = data.product.whitelist?.includes(key);
                return (
                  <button key={key} onClick={() => toggleWhitelist(key)} data-testid={`whitelist-${m.provider}-${m.id}`}
                    className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border ${
                      on ? TIER_TONE[m.tier] : "border-gray-200 bg-white text-gray-500 hover:border-gray-400"
                    }`}>
                    {on && <CheckCircle size={10} weight="fill" />}
                    <span className="font-bold">{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── Tenant overrides ─── */}
        <section className="border border-gray-200 bg-white" data-testid="tenant-llm-section">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Tenant Overrides</div>
              <h3 className="text-xl font-black tracking-tight mt-0.5">Per-tenant model + BYOK</h3>
            </div>
            <div className="flex gap-2">
              <input value={newTenantId} onChange={(e) => setNewTenantId(e.target.value)}
                placeholder="co:vakuai" data-testid="new-tenant-id"
                className="border border-gray-300 px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
              <button onClick={() => { if (newTenantId) { setEditTenant(newTenantId); setNewTenantId(""); } }}
                data-testid="add-tenant-override"
                className="border border-[#0020C2] text-[#0020C2] px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#0020C2] hover:text-white">
                + Override
              </button>
            </div>
          </div>
          <table className="w-full text-sm" data-testid="tenant-llm-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Tenant", "Parse", "Suggest", "Insights", "BYOK", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.tenants.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                  No tenant overrides yet. Every tenant uses the product defaults above.
                </td></tr>
              )}
              {data.tenants.map((t) => (
                <tr key={t.tenant_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`tenant-row-${t.tenant_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{t.tenant_id}</td>
                  <td className="px-4 py-3 text-xs font-mono">{t.parse?.id || <span className="text-gray-400">— default —</span>}</td>
                  <td className="px-4 py-3 text-xs font-mono">{t.suggest?.id || <span className="text-gray-400">— default —</span>}</td>
                  <td className="px-4 py-3 text-xs font-mono">{t.insights?.id || <span className="text-gray-400">— default —</span>}</td>
                  <td className="px-4 py-3 text-xs font-mono">
                    {t.byok_set
                      ? <span className="inline-flex items-center gap-1 text-emerald-700"><Lock size={10} weight="fill" /> {t.byok_provider}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => setEditTenant(t.tenant_id)} data-testid={`edit-tenant-${t.tenant_id}`}
                      className="text-[11px] font-bold uppercase tracking-wider text-[#0020C2] hover:underline">Edit</button>
                    <button onClick={() => clearTenant(t.tenant_id)} data-testid={`clear-tenant-${t.tenant_id}`}
                      className="text-[11px] font-bold uppercase tracking-wider text-[#DC2626] hover:underline inline-flex items-center gap-1">
                      <ArrowCounterClockwise size={11} weight="bold" /> Reset
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
      {editTenant && (
        <TenantDrawer
          tenantId={editTenant}
          settings={data.tenants.find((t) => t.tenant_id === editTenant)}
          catalog={data.catalog}
          onClose={() => setEditTenant(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
